/**
 * Stack — flex layout helper. Use instead of `<div style={{ display:'flex', gap:8 }}>` everywhere.
 *
 *   <Stack gap="md">child1 child2 child3</Stack>
 *   <Stack direction="row" gap={12} align="center" justify="between">…</Stack>
 */
import React from 'react';

type Gap = number | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
const GAP_MAP: Record<Exclude<Gap, number>, number> = {
  xs: 4, sm: 8, md: 12, lg: 16, xl: 24,
};

type Align = 'start' | 'center' | 'end' | 'stretch' | 'baseline';
type Justify = 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly';

const ALIGN_MAP: Record<Align, string> = {
  start: 'flex-start', center: 'center', end: 'flex-end', stretch: 'stretch', baseline: 'baseline',
};
const JUSTIFY_MAP: Record<Justify, string> = {
  start: 'flex-start', center: 'center', end: 'flex-end',
  between: 'space-between', around: 'space-around', evenly: 'space-evenly',
};

export interface StackProps extends React.HTMLAttributes<HTMLDivElement> {
  direction?: 'row' | 'column';
  gap?: Gap;
  align?: Align;
  justify?: Justify;
  wrap?: boolean;
  inline?: boolean;
  fullWidth?: boolean;
  as?: React.ElementType;
}

export const Stack: React.FC<StackProps> = ({
  direction = 'column',
  gap = 'md',
  align,
  justify,
  wrap,
  inline,
  fullWidth,
  as: Tag = 'div',
  style,
  children,
  className,
  ...rest
}) => {
  const gapPx = typeof gap === 'number' ? gap : GAP_MAP[gap];
  return (
    <Tag
      style={{
        display: inline ? 'inline-flex' : 'flex',
        flexDirection: direction,
        gap: gapPx,
        alignItems: align ? ALIGN_MAP[align] : undefined,
        justifyContent: justify ? JUSTIFY_MAP[justify] : undefined,
        flexWrap: wrap ? 'wrap' : undefined,
        width: fullWidth ? '100%' : undefined,
        ...style,
      }}
      className={className}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export default Stack;

